import React from 'react';
import { Card, CardContent, Typography, Box, Tooltip, Chip } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  WarningAmber as WarningAmberIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { REPS_COLORS, SEVERITY_COLORS } from '../utils/constants';
import { getSeverity, getScoreColor } from '../utils/scoring';
import { bandLegend } from '../utils/bands';

const SEVERITY_LABELS = {
  healthy: 'Healthy',
  monitor: 'Monitor',
  warning: 'Warning',
};

function REPSQuadrant({ label, score, baseScore, adjustmentDelta = 0, adjustmentsApplied = 0, quadrantKey, onClick, warning, threshold = 6, perception = null, reality = null, basis = null, mode = 'conditions' }) {
  // Conditions-only (Tim 20 Oct): the score IS the approved OE Conditions; surveys shown as a reference line.
  // Blend (Beta-1 P2-35): Perception (surveys) · Reality (approved OE Conditions) — shown when the snapshot carries the split.
  const conditionsOnly = mode === 'conditions';
  const hasSplit = !conditionsOnly && perception != null && reality != null;
  const hasAdjustment = adjustmentDelta !== 0 && adjustmentsApplied > 0;
  const color = REPS_COLORS[quadrantKey] || '#757575';
  const severity = score != null ? getSeverity(score, threshold) : null;
  const severityColor = score != null ? getScoreColor(score, threshold) : null;
  const legend = bandLegend(threshold);
  const severityLabel = severity ? SEVERITY_LABELS[severity] : null;
  const hasWarning = !!warning;
  const warningColor = '#c62828';

  return (
    <Card
      sx={{
        borderTop: `4px solid ${hasWarning ? warningColor : color}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.3s',
        position: 'relative',
        ...(hasWarning && {
          boxShadow: `0 0 0 1px ${warningColor}40`,
          bgcolor: `${warningColor}04`,
          animation: 'warningPulse 3s ease-in-out infinite',
          '@keyframes warningPulse': {
            '0%, 100%': { boxShadow: `0 0 0 1px ${warningColor}40` },
            '50%': { boxShadow: `0 0 8px 2px ${warningColor}30` },
          },
        }),
        '&:hover': onClick
          ? { boxShadow: 6, transform: 'translateY(-2px)' }
          : {},
      }}
      onClick={onClick}
    >
      {/* Warning alert icon badge */}
      {hasWarning && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <Tooltip title={`Threshold exceeded: ${(warning.score || 0).toFixed(1)} > ${warning.threshold}`}>
            <WarningAmberIcon
              sx={{
                color: warningColor,
                fontSize: 22,
                filter: 'drop-shadow(0 1px 2px rgba(198,40,40,0.4))',
                animation: 'warningIconPulse 2s ease-in-out infinite',
                '@keyframes warningIconPulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.6 },
                },
              }}
            />
          </Tooltip>
        </Box>
      )}
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography
          variant="subtitle2"
          color={hasWarning ? 'error' : 'text.secondary'}
          sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem', fontWeight: hasWarning ? 700 : 400 }}
        >
          {label}
        </Typography>
        {score != null ? (
          <>
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Roboto Mono", monospace',
                fontWeight: 700,
                color: severityColor || color,
                mt: 1,
                mb: 0.5,
              }}
            >
              {score.toFixed(1)}
            </Typography>
            {/* Adjustment delta indicator */}
            {hasAdjustment && (
              <Tooltip
                title={`Composite score: ${(baseScore ?? 0).toFixed(1)} base ${adjustmentDelta > 0 ? '+' : ''}${adjustmentDelta.toFixed(1)} from ${adjustmentsApplied} audit adjustment${adjustmentsApplied !== 1 ? 's' : ''}. The composite score combines survey data with insights from document audits.`}
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'help',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: adjustmentDelta > 0 ? '#d32f2f' : '#2e7d32',
                      fontWeight: 700,
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: '0.8rem',
                    }}
                  >
                    ({adjustmentDelta > 0 ? '+' : ''}{adjustmentDelta.toFixed(1)} from audits)
                  </Typography>
                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.6 }} />
                </Box>
              </Tooltip>
            )}
            <Tooltip title={`Score: ${score.toFixed(1)} — ${severityLabel} (${legend[severity] || ''})`}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 10,
                  bgcolor: `${severityColor}15`,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: severityColor,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: severityColor, fontWeight: 600 }}
                >
                  {severityLabel}
                </Typography>
              </Box>
            </Tooltip>
            {conditionsOnly && perception != null && (
              <Tooltip title={`Score = approved OE Conditions (Drift, Traps, Roadblocks, Landmines) in this scope, severity-weighted. Surveys measure perception: ${perception.toFixed(1)} here. Divergence between the two is what DREW watches; perception never moves the score.`}>
                <Typography variant="caption" display="block" sx={{ mt: 0.75, color: 'text.secondary', fontFamily: '"Roboto Mono", monospace', fontSize: '0.68rem' }}>
                  Surveys (perception) {perception.toFixed(1)}
                </Typography>
              </Tooltip>
            )}
            {hasSplit && (
              <Tooltip title={`Perception = survey score ${perception.toFixed(1)}; Reality = +${reality.toFixed(1)} from approved OE Conditions in this scope (capped share). Score = Perception + Reality.`}>
                <Typography variant="caption" display="block" sx={{ mt: 0.75, color: 'text.secondary', fontFamily: '"Roboto Mono", monospace', fontSize: '0.68rem' }}>
                  Perception {perception.toFixed(1)} · Reality +{reality.toFixed(1)}
                </Typography>
              </Tooltip>
            )}
            {basis?.text && (
              <Tooltip title={conditionsOnly ? 'Approved and Resolution-pending OE Conditions count in full; Resolved ones fade out over the configured days. Pending, Not Approved, Withdrawn and Cancelled never count.' : basis.lowConfidence ? 'Low confidence: fewer than 5 responses or under 40 % of the people in this scope responded.' : 'Headcount-weighted rollup of the last 14 days of responses.'}>
                <Typography variant="caption" display="block" sx={{ color: basis.lowConfidence ? 'warning.main' : 'text.disabled', fontSize: '0.65rem', mt: 0.25 }}>
                  {basis.lowConfidence ? '⚠ ' : ''}{basis.text}
                </Typography>
              </Tooltip>
            )}
            {/* Threshold exceeded chip */}
            {hasWarning && (
              <Chip
                icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                label={`Exceeds threshold (${warning.threshold})`}
                size="small"
                color="error"
                variant="outlined"
                sx={{ mt: 1, fontSize: '0.7rem', fontWeight: 600 }}
              />
            )}
          </>
        ) : (
          <Box sx={{ py: 2 }}>
            <TrendingUpIcon sx={{ fontSize: 32, color: `${color}40`, mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              No data yet
            </Typography>
          </Box>
        )}
        {onClick && (
          /* The card itself is the control (hover lift + arrow) — the old "Click for details"
             caption was a crutch (UI review 2026-09-21 §5). */
          <Box aria-hidden sx={{ position: 'absolute', right: 10, bottom: 8, opacity: 0.45, display: 'flex' }}>
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default REPSQuadrant;
